import { Empty } from 'antd';
import { Card, Text } from '@bancobarigui/barihaus';
import { HourglassOutlined, UserOutlined } from '@ant-design/icons';

import { InspectionRequest } from '@/services/types/inspection.types';

interface InspectionAssignmentsProps {
  inspection: InspectionRequest;
}

interface Assignment {
  id: string;
  type: 'bank' | 'provider' | 'inspector';
  title: string;
  subtitle?: string | undefined;
  userName?: string | undefined;
  date?: string | undefined;
  status?: 'completed' | 'pending' | undefined;
}

export function InspectionAssignments({ inspection }: InspectionAssignmentsProps) {

  const firstItem = inspection.items?.[0];
  const workflow = firstItem?.workflow;

  const assignments: Assignment[] = [];

  if (workflow?.assignmentWorkflow && workflow.assignmentWorkflow.length > 0) {
    workflow.assignmentWorkflow.forEach((assignment, index) => {
      const type = assignment.toRole === 'Prestadora' ? 'provider' : 'inspector';
      
      assignments.push({
        id: `assignment-${index}`,
        type: type as 'provider' | 'inspector',
        title: assignment.toName,
        subtitle: assignment.notes || undefined,
        userName: assignment.fromUserName,
        date: new Date(assignment.occurredAt).toLocaleString('pt-BR'),
        status: 'completed' as const,
      });
    });
  }

  const hasAssignments = assignments.length > 0;

  const getAssignmentIcon = (type: string) => {
    switch (type) {
      case 'bank':
        return <UserOutlined className="text-blue-500" />;
      case 'provider':
        return <HourglassOutlined className="text-orange-500" />;
      case 'inspector':
        return <UserOutlined className="text-green-500" />;
      default:
        return <UserOutlined className="text-gray-500" />;
    }
  };

  const getAssignmentIconBg = (type: string) => {
    switch (type) {
      case 'bank':
        return 'bg-blue-100';
      case 'provider':
        return 'bg-orange-100';
      case 'inspector':
        return 'bg-green-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getAssignmentTitle = (type: string) => {
    switch (type) {
      case 'bank':
        return 'Banco';
      case 'provider':
        return 'Prestadora';
      case 'inspector':
        return 'Inspetor';
      default:
        return 'Direcionamento';
    }
  };

  return (
    <div className="space-y-6">
      {!hasAssignments ? (
        <div className="flex items-center justify-center py-20">
          <Empty
            description={
              <div className="space-y-2">
                <Text className="text-gray-500 block">Nenhum direcionamento registrado</Text>
                <Text className="text-gray-400 text-sm">
                  Os direcionamentos aparecerão aqui quando a inspeção for direcionada
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assignments.map((assignment) => (
          <div key={assignment.id} className="flex flex-col">
            <h3 className="mb-3 text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {getAssignmentTitle(assignment.type)}
            </h3>
            <Card className="shadow-sm border border-gray-200 flex-1 h-full">
              <div className="flex items-start gap-4 h-full">
                <div className={`flex-shrink-0 w-10 h-10 ${getAssignmentIconBg(assignment.type)} rounded-full flex items-center justify-center`}>
                  {getAssignmentIcon(assignment.type)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{assignment.title}</p>
                  {assignment.subtitle && (
                    <p className="text-sm text-gray-500 mt-1">{assignment.subtitle}</p>
                  )}
                  {assignment.userName && assignment.date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Direcionado por <span className="font-medium text-gray-600">{assignment.userName}</span>{' '}
                      em {assignment.date}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        ))}
        </div>
      )}

      <Card className="shadow-sm bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="text-blue-500 mt-0.5">
            <HourglassOutlined className="text-lg" />
          </div>
          <div>
            <Text strong className="text-blue-900 block">
              Histórico de Direcionamentos
            </Text>
            <Text className="text-blue-700 text-sm">
              Esta seção mostra o histórico completo de todos os direcionamentos da inspeção,
              incluindo banco, prestadoras e inspetores designados ao longo do processo.
            </Text>
          </div>
        </div>
      </Card>

    </div>
  );
}
